import ReorderIcon from '../Icons/ReorderIcon.svg';
import { logClick } from 'ff/common/SegmentAnalytics';
import {
    fetchMenuItemOrder,
    getLocalMenuItemOrder,
    pushOrderToServer,
    saveMenuItemOrder,
    setLocalMenuItemOrder,
} from './ReorderPersistence';
import { RenderMenuParams } from 'ff/common/components/FFMenuV2/RenderMenuParams';

// Whether an item can be picked up and dragged. Opt-in per item (see MenuItems/index.ts) —
// items like JetFuelX/Recap are locked in place but stay droppable; Account/Get Help/
// Support/Logout/sub-section-headers are neither.
const DRAGGABLE_SELECTOR = 'a.ffmenu-item.ffmenu-draggable[data-menu-item-id]';
// Whether an item is a valid drop target. Kept independent of draggability so a locked
// item (e.g. JetFuelX) can still be dropped *on*, keeping its divider-bound section
// reachable even if every draggable item around it moves away.
const DROPPABLE_SELECTOR = 'a.ffmenu-item.ffmenu-droppable[data-menu-item-id]';
// Dividers aren't draggable themselves, but their position relative to the items
// around them needs to be preserved, so they're tracked alongside draggable/droppable
// items wherever order is captured/applied. Droppable-only anchors (JetFuelX/Recap) are
// included here too so their position survives reconstruction instead of being pinned
// to a raw DOM slot index, same as dividers.
const DIVIDER_SELECTOR = 'hr';
const SORTABLE_SELECTOR = `${DRAGGABLE_SELECTOR}, ${DROPPABLE_SELECTOR}, ${DIVIDER_SELECTOR}`;
const REORDER_CONTAINER_SELECTOR =
    '#ffmenu-scrolling-wrapper, .sub-section-contents';

let reorderModeActive = false;
let draggedEl: HTMLElement | null = null;

// Dividers have no natural unique id, but they can never be reordered relative to one
// another (only draggable items can move around them), so numbering them by their
// position in document order gives each one a stable, consistent id across calls.
function buildSortableIdMap(root: ShadowRoot): Map<Element, string> {
    const idMap = new Map<Element, string>();
    let dividerIndex = 0;
    root.querySelectorAll<HTMLElement>(SORTABLE_SELECTOR).forEach(el => {
        idMap.set(
            el,
            el.matches(DIVIDER_SELECTOR)
                ? `divider-${dividerIndex++}`
                : el.getAttribute('data-menu-item-id')!
        );
    });
    return idMap;
}

function getCurrentOrder(root: ShadowRoot): string[] {
    const idMap = buildSortableIdMap(root);
    return Array.from(
        root.querySelectorAll<HTMLElement>(SORTABLE_SELECTOR)
    ).map(el => idMap.get(el)!);
}

function reorderDom(root: ShadowRoot, savedOrder: string[]) {
    if (!savedOrder.length) {
        return;
    }
    const idMap = buildSortableIdMap(root);
    const containers = [
        root.getElementById('ffmenu-scrolling-wrapper'),
        ...Array.from(
            root.querySelectorAll<HTMLElement>('.sub-section-contents')
        ),
    ].filter((el): el is HTMLElement => !!el);

    containers.forEach(container => {
        const originalChildren = Array.from(container.children);
        const items = originalChildren.filter((el): el is HTMLElement =>
            el.matches(SORTABLE_SELECTOR)
        );
        const sorted = [...items].sort((a, b) => {
            const aIndex = savedOrder.indexOf(idMap.get(a)!);
            const bIndex = savedOrder.indexOf(idMap.get(b)!);
            const aRank = aIndex === -1 ? Infinity : aIndex;
            const bRank = bIndex === -1 ? Infinity : bIndex;
            if (aRank !== bRank) {
                return aRank - bRank;
            }
            //Items with no saved position keep their original relative order.
            return items.indexOf(a) - items.indexOf(b);
        });
        //Rebuild the full child list in its original slot order, but pull sortable
        //slots from `sorted` instead of their original element — this keeps truly fixed
        //elements (fill-space, the ignored Support/GetHelp/Logout items) pinned in place
        //instead of getting pushed to the end by appendChild.
        let sortedIndex = 0;
        originalChildren.forEach(el => {
            container.appendChild(
                el.matches(SORTABLE_SELECTOR) ? sorted[sortedIndex++] : el
            );
        });
    });
}

export function applySavedMenuItemOrder(
    root: ShadowRoot,
    params: Pick<RenderMenuParams, 'currentUser'>
): Promise<void> {
    // Optimistically apply whatever's in localStorage right away so the menu doesn't
    // flash into its default order while we wait on the network.
    let localPayload = getLocalMenuItemOrder(params);
    if (localPayload) {
        reorderDom(root, localPayload.order);
    }

    return fetchMenuItemOrder().then(serverPayload => {
        if (!serverPayload) {
            return;
        }

        //Get localPayload again in case it has been updated while fetchMenuItemOrder
        // was in flight.
        //Use case: local version timestamp is 1, server timestamp is 2.
        // Page is loaded, local is set, request server, while waiting user modifies
        // things, local is now 3. Server request comes back, we dont want that to
        // be used. We want to keep the local version, since it is now newer.
        localPayload = getLocalMenuItemOrder(params);
        if (!localPayload || serverPayload.timestamp > localPayload.timestamp) {
            //The server has the newer version; adopt it locally.
            reorderDom(root, serverPayload.order);
            setLocalMenuItemOrder(serverPayload, params);
        } else if (localPayload.timestamp > serverPayload.timestamp) {
            //localStorage has the newer version; push it up to the server.
            pushOrderToServer(localPayload);
        }
    });
}

export function appendReorderButton(root: ShadowRoot) {
    const collapseBtn = root.getElementById('collapse-btn');
    if (!collapseBtn) {
        return;
    }
    const img = document.createElement('img');
    img.setAttribute('id', 'reorder-btn');
    img.setAttribute('src', ReorderIcon);
    img.addEventListener('click', event => {
        // Stop this from bubbling up to #collapse-btn's own click handler, which would
        // otherwise also collapse/expand the menu.
        event.stopPropagation();
        logClick(img, 'FFMenu, Toggle Reorder Mode Btn');
        toggleReorderMode(root);
    });
    // Insert before the existing collapse arrow so both icons stay right-aligned together.
    collapseBtn.insertBefore(img, collapseBtn.firstChild);
}

export function toggleReorderMode(root: ShadowRoot) {
    reorderModeActive = !reorderModeActive;
    applyReorderModeState(root);
}

export function exitReorderMode(root: ShadowRoot) {
    if (!reorderModeActive) {
        return;
    }
    reorderModeActive = false;
    applyReorderModeState(root);
}

function applyReorderModeState(root: ShadowRoot) {
    const scrollingWrapper = root.getElementById('ffmenu-scrolling-wrapper');
    const reorderBtn = root.getElementById('reorder-btn');
    scrollingWrapper?.classList.toggle('x-reorder-mode', reorderModeActive);
    reorderBtn?.classList.toggle('active', reorderModeActive);

    root.querySelectorAll<HTMLAnchorElement>(DRAGGABLE_SELECTOR).forEach(el => {
        el.classList.toggle('reorderable', reorderModeActive);
        // Links and images are natively draggable in most browsers even without a
        // `draggable` attribute, so exiting reorder mode must explicitly set
        // draggable="false" rather than just removing the attribute.
        el.setAttribute('draggable', reorderModeActive ? 'true' : 'false');
    });
}

export function initReorderMode(
    root: ShadowRoot,
    params: Pick<RenderMenuParams, 'currentUser'>
) {
    const scrollingWrapper = root.getElementById('ffmenu-scrolling-wrapper');
    if (!scrollingWrapper) {
        return;
    }

    scrollingWrapper.addEventListener('dragstart', event => {
        if (!reorderModeActive) {
            return;
        }
        const target = (event.target as HTMLElement)?.closest<HTMLElement>(
            DRAGGABLE_SELECTOR
        );
        if (!target) {
            return;
        }
        draggedEl = target;
        target.classList.add('dragging');
        event.dataTransfer?.setData('text/plain', '');
        if (event.dataTransfer) {
            event.dataTransfer.effectAllowed = 'move';
        }
    });

    scrollingWrapper.addEventListener('dragover', event => {
        if (!draggedEl) {
            return;
        }
        // Mark the drop as accepted so the browser doesn't play its "snap back to origin"
        // ghost animation on release.
        event.preventDefault();
        if (event.dataTransfer) {
            event.dataTransfer.dropEffect = 'move';
        }
        const target = (event.target as HTMLElement)?.closest<HTMLElement>(
            DROPPABLE_SELECTOR
        );
        if (!target || target === draggedEl) {
            return;
        }
        if (
            draggedEl.closest(REORDER_CONTAINER_SELECTOR) !==
            target.closest(REORDER_CONTAINER_SELECTOR)
        ) {
            //Don't allow dragging items between different reorder containers (e.g. across
            // org-admin sub-sections).
            return;
        }
        const rect = target.getBoundingClientRect();
        const isAfter = event.clientY - rect.top > rect.height / 2;
        target.parentElement?.insertBefore(
            draggedEl,
            isAfter ? target.nextSibling : target
        );
    });

    scrollingWrapper.addEventListener('drop', event => {
        if (!draggedEl) {
            return;
        }
        //Prevent the browser from treating the drop as link navigation.
        event.preventDefault();
    });

    scrollingWrapper.addEventListener('dragend', () => {
        if (draggedEl) {
            draggedEl.classList.remove('dragging');
            draggedEl = null;
            saveMenuItemOrder(getCurrentOrder(root), params);
        }
    });
}
